import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './App.scss';
import 'bootstrap/dist/css/bootstrap.css';
import reportWebVitals from './reportWebVitals';
import { RouterProvider } from 'react-router-dom';
import routerBrowser from './Routes';
import { ContextProvider } from './Context';
import { bootstrapAuth } from './Services/Api/client';
import { deleteCookie } from './Services/Cookie';
import { startVersionWatcher } from './Services/VersionCheck';

// One-time cleanup of the cookies retired when the session moved into context.
// Nothing writes or reads them now; this clears the ones browsers still hold.
// Safe to drop a release after deploy.
const RETIRED_COOKIES = [
  'firstName', 'lastName', 'middleName', 'token', 'userName', 'isActive',
  'showClinical', 'personalEmail', 'muEmail', 'emailType', 'facultyId',
  'displayName', 'shortName', 'contactEmail', 'isMentor', 'isPartTime',
  'SemesterId', 'hasSurveyPermission',
];
RETIRED_COOKIES.forEach((name) => {
  deleteCookie(name, '/');
  deleteCookie(name);
});
localStorage.removeItem('profile_image');

// Clickjacking defense-in-depth: refuse to run inside a cross-origin frame.
// Primary protection is the HTTP headers set in public/.htaccess; this covers
// legacy browsers and any host where mod_headers is unavailable.
// Same-origin framing is allowed, matching frame-ancestors 'self' / SAMEORIGIN.
if (window.top !== window.self) {
  let sameOriginTop = false;
  try {
    sameOriginTop = window.top.location.origin === window.self.location.origin;
  } catch {
    // Reading a cross-origin top's location throws: treat as untrusted.
    sameOriginTop = false;
  }

  if (!sameOriginTop) {
    try {
      window.top.location = window.self.location.href;
    } catch {
      // Cross-origin top we can't redirect: neutralize the page instead.
      document.documentElement.innerHTML =
        'This application cannot be displayed in a frame.';
    }
  }
}

/**
 * Recovers the session before any route renders.
 *
 * The access token lives only in memory, so it is gone after every page load
 * and has to be re-obtained from the refresh cookie. Routes must not render
 * until that settles: they would fire API calls with no credential, each
 * getting a 401 and tearing down a session that was actually still valid.
 *
 * A failed refresh is the ordinary logged-out case, not an error -- rendering
 * proceeds either way, and the route guards send the user to login.
 */
const AuthBootstrap = ({ children }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    bootstrapAuth().finally(() => {
      if (active) setReady(true);
    });
    return () => { active = false; };
  }, []);

  if (!ready) return null;
  return children;
};

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  // <Suspense fallback={fallback()}>
  <ContextProvider value="test">
    <AuthBootstrap>
      <RouterProvider router={routerBrowser} />
    </AuthBootstrap>
  </ContextProvider>
  // </Suspense>
);

// Watch for a newly deployed build and prompt the user to reload into it.
// No-op outside production builds.
startVersionWatcher();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();